import { z } from 'zod';
/**
 * 通知の宛先の環境（#233、ADR-029）。APNs は環境ごとにホストが違い、トークンも環境ごとに違う。
 *
 * - `sandbox`: Xcode から入れた Debug のビルド
 * - `production`: TestFlight と App Store のビルド
 *
 * **アプリがビルド構成から決めて送る。** サーバーはトークンだけでは見分けられない。
 */
export const PUSH_ENVIRONMENTS = ['sandbox', 'production'];
/**
 * APNs のデバイストークン（16 進）。
 *
 * **長さを 64 桁に決め打ちしない。** Apple はトークンの長さが変わりうるとしている（今は 32 バイト = 64 桁）。
 * バイト列の 16 進なので桁数は偶数。上限は、でたらめに長い値で D1 を埋めさせないため。
 */
export const pushTokenSchema = z
    .string()
    .min(64)
    .max(200)
    .regex(/^(?:[0-9a-fA-F]{2})+$/, 'デバイストークンは 16 進の文字列にしてください');
/** 通知の宛先を登録する（`PUT /api/v1/me/push-token`）。持ち主が別のアカウントなら付け替える。 */
export const registerPushTokenSchema = z.object({
    token: pushTokenSchema,
    environment: z.enum(PUSH_ENVIRONMENTS),
});
/**
 * 通知の宛先を消す（`DELETE /api/v1/me/push-token`。通知をオフにしたとき）。
 *
 * **トークンは URL ではなく本文で送る。** パスに入れると、アクセスログ（`hono/logger`）にトークンが出る。
 */
export const unregisterPushTokenSchema = z.object({
    token: pushTokenSchema,
});
/** 登録 / 削除の結果。このあとその端末に送るかどうか。 */
export const pushTokenStateSchema = z.object({
    enabled: z.boolean(),
});
/**
 * 大会の通知の種類（ADR-029 §3）。
 *
 * - `tournament_match_starting`: 自分の試合がもうすぐ始まる（2 回戦以降の約 1 分前）
 * - `tournament_finished`: 大会が終わった（負けた人にも送る）
 */
export const TOURNAMENT_PUSH_TYPES = ['tournament_match_starting', 'tournament_finished'];
/**
 * 大会の通知の本文（APNs のペイロード）。アプリは `tournamentId` で着地する大会の画面を決め、
 * 開いている大会の画面と同じなら出さない（ADR-029 §8）。
 *
 * **文面（`aps.alert.body`）に勝敗・ラウンド・順位を書かない**（#230 の「見るまで伏せる」。ADR-029 §4）。
 *
 * 形は契約フィクスチャ（`packages/contracts/mobile/tournaments.push.*.json`）でサーバーと iOS を突き合わせる。
 */
export const tournamentPushPayloadSchema = z.object({
    aps: z.object({
        alert: z.object({ body: z.string().min(1) }),
        sound: z.literal('default'),
        /** 通知センターで大会ごとにまとめる。大会の ID。 */
        'thread-id': z.string().min(1),
    }),
    type: z.enum(TOURNAMENT_PUSH_TYPES),
    tournamentId: z.string().min(1),
});
